// Category definitions and game-to-category mappings
import { getCategoryLabel } from '../i18n/helpers';

export interface CategoryInfo {
  value: string;
  label: string;
  icon: string;
}

function category(value: string, icon: string): CategoryInfo {
  const info: CategoryInfo = { value, label: '', icon };
  Object.defineProperty(info, 'label', {
    get: () => getCategoryLabel(value),
    enumerable: true,
    configurable: true,
  });
  return info;
}

// All category definitions (labels resolved via i18n at access time)
export const CATEGORY_DEFINITIONS: Record<string, CategoryInfo> = {
  nature: category('nature', '🌿'),
  science: category('science', '🔬'),
  food: category('food', '🍕'),
  history: category('history', '📜'),
  entertainment: category('entertainment', '🎭'),
  geography: category('geography', '🌍'),
  sports: category('sports', '⚽'),
  cars: category('cars', '🚗'),
  music: category('music', '🎵'),
  technology: category('technology', '💻'),
  space: category('space', '🚀'),
  literature: category('literature', '📚'),
  movies: category('movies', '🎬'),
  art: category('art', '🎨'),
  landmarks: category('landmarks', '🏛️'),
  disney: category('disney', '🏰'),
  mythology: category('mythology', '⚡'),
  inventions: category('inventions', '🔧'),
  'world-records': category('world-records', '🏆'),
  // Agile Quiz categories
  scrum: category('scrum', '📋'),
  kanban: category('kanban', '📌'),
  agile: category('agile', '🔄'),
  devops: category('devops', '🚀'),
  estimation: category('estimation', '📊'),
  satire: category('satire', '😏'),
  tech: category('tech', '💻'),
  culture: category('culture', '👥'),
  metrics: category('metrics', '📈'),
  engineering: category('engineering', '⚙️'),
  product: category('product', '🎯'),
  security: category('security', '🔒'),
  architecture: category('architecture', '🏗️'),
  design: category('design', '🎨'),
  testing: category('testing', '🧪'),
  data: category('data', '📉'),
  ai: category('ai', '🤖'),
  hr: category('hr', '👤'),
  business: category('business', '💼'),
  sales: category('sales', '📞'),
  marketing: category('marketing', '📣'),
  // It's Funny categories
  'office-humor': category('office-humor', '📧'),
  'remote-humor': category('remote-humor', '🏠'),
  'manager-humor': category('manager-humor', '👔'),
  'dev-humor': category('dev-humor', '💻'),
  'designer-humor': category('designer-humor', '🎨'),
  'executive-humor': category('executive-humor', '📊'),
  'hr-humor': category('hr-humor', '📋'),
  'recruiting-humor': category('recruiting-humor', '🔍'),
  'sales-humor': category('sales-humor', '📞'),
  'startup-humor': category('startup-humor', '🚀'),
  'support-humor': category('support-humor', '🎧'),
};

// Get available categories for a specific game type
export const getCategoriesForGame = (gameType: string): CategoryInfo[] => {
  switch (gameType) {
    case 'trivia':
    case 'tech-trivia':
      return [
        CATEGORY_DEFINITIONS.nature,
        CATEGORY_DEFINITIONS.science,
        CATEGORY_DEFINITIONS.food,
        CATEGORY_DEFINITIONS.history,
        CATEGORY_DEFINITIONS.entertainment,
        CATEGORY_DEFINITIONS.geography,
        CATEGORY_DEFINITIONS.sports,
        CATEGORY_DEFINITIONS.cars,
        CATEGORY_DEFINITIONS.music,
        CATEGORY_DEFINITIONS.technology,
        CATEGORY_DEFINITIONS.space,
        CATEGORY_DEFINITIONS.literature,
        CATEGORY_DEFINITIONS.movies,
        CATEGORY_DEFINITIONS.mythology,
        CATEGORY_DEFINITIONS.inventions,
        CATEGORY_DEFINITIONS['world-records'],
      ];
    case 'picture-perfect':
      return [
        CATEGORY_DEFINITIONS.art,
        CATEGORY_DEFINITIONS.history,
        CATEGORY_DEFINITIONS.landmarks,
        CATEGORY_DEFINITIONS.nature,
        CATEGORY_DEFINITIONS.entertainment,
        CATEGORY_DEFINITIONS.sports,
        CATEGORY_DEFINITIONS.music,
        CATEGORY_DEFINITIONS.science,
        CATEGORY_DEFINITIONS.cars,
        CATEGORY_DEFINITIONS.technology,
        CATEGORY_DEFINITIONS.geography,
        CATEGORY_DEFINITIONS.food,
        CATEGORY_DEFINITIONS.space,
        CATEGORY_DEFINITIONS.literature,
        CATEGORY_DEFINITIONS.movies,
        CATEGORY_DEFINITIONS.disney,
      ];
    case 'close-or-not':
      return [
        CATEGORY_DEFINITIONS.history,
        CATEGORY_DEFINITIONS.technology,
        CATEGORY_DEFINITIONS.entertainment,
        CATEGORY_DEFINITIONS.science,
        CATEGORY_DEFINITIONS.geography,
        CATEGORY_DEFINITIONS.nature,
        CATEGORY_DEFINITIONS.sports,
        CATEGORY_DEFINITIONS.music,
      ];
    case 'word-puzzle':
      return [
        CATEGORY_DEFINITIONS.literature,
        CATEGORY_DEFINITIONS.movies,
        CATEGORY_DEFINITIONS.music,
        CATEGORY_DEFINITIONS.history,
        CATEGORY_DEFINITIONS.science,
        CATEGORY_DEFINITIONS.geography,
        CATEGORY_DEFINITIONS.entertainment,
        CATEGORY_DEFINITIONS.sports,
      ];
    case 'speed-challenge':
      return [
        CATEGORY_DEFINITIONS.entertainment,
        CATEGORY_DEFINITIONS.sports,
        CATEGORY_DEFINITIONS.music,
        CATEGORY_DEFINITIONS.movies,
        CATEGORY_DEFINITIONS.geography,
        CATEGORY_DEFINITIONS.history,
        CATEGORY_DEFINITIONS.science,
        CATEGORY_DEFINITIONS.food,
      ];
    case 'fact-or-myth':
      return [
        CATEGORY_DEFINITIONS.science,
        CATEGORY_DEFINITIONS.history,
        CATEGORY_DEFINITIONS.geography,
        CATEGORY_DEFINITIONS.nature,
        CATEGORY_DEFINITIONS.entertainment,
        CATEGORY_DEFINITIONS.sports,
        CATEGORY_DEFINITIONS.technology,
        CATEGORY_DEFINITIONS.space,
      ];
    case 'almost-genius':
      return [
        category('everyday-trap', '🌍'),
        category('reading-trap', '📖'),
        category('logic-trap', '🧩'),
        category('math-trap', '🔢'),
        category('history-trap', '📜'),
        category('science-trap', '🔬'),
        category('words', '📝'),
        category('truthy', '✅'),
        category('gotcha', '🎯'),
        category('past', '⏳'),
        category('semantics', '💬'),
        category('logic', '🧠'),
        category('twist', '🌀'),
        category('brainhurt', '🤯'),
        category('digits', '🔢'),
        category('duh', '😅'),
        category('skim', '👀'),
        category('facepalm', '🤦'),
        category('groan', '😤'),
        category('arithmetic', '➕'),
        category('nerdy', '🤓'),
        category('facts', '📚'),
        category('numbers', '🔢'),
      ];
    case 'are-you-drunk':
    case 'vision-check':
      return [
        category('relationships', '💕'),
        category('pop-culture', '🎬'),
        category('embarrassing', '😳'),
        category('wild', '🔥'),
        category('funny', '😂'),
        category('quirky', '🤪'),
        category('sexy', '😏'),
        category('kinky', '😈'),
        category('all', '🎲'),
      ];
    case 'play-it-loud':
      return [
        category('pop', '🎤'),
        category('hip-hop', '🎧'),
        category('rock', '🎸'),
        category('latin', '🌮'),
        category('edm', '⚡'),
        category('rnb', '🎹'),
        category('country', '🤠'),
        category('kpop', '💫'),
        category('afrobeats', '🌍'),
        category('phonk', '🎚️'),
        category('classical', '🎼'),
        category('metal', '🤘'),
        category('reggae', '🎵'),
        category('jazz', '🎷'),
        category('blues', '🎸'),
        category('punk', '🎸'),
        category('folk', '🪕'),
        category('funk', '🎸'),
        category('lofi', '☕'),
        category('soundtrack', '🎬'),
      ];
    case 'kinky-or-not':
      return [
        category('kinky', '😈'),
        category('relationships', '💕'),
        category('sex', '🔥'),
        category('embarrassing', '😳'),
        category('spicy', '🌶️'),
        category('steamy', '💨'),
      ];
    case 'drinking-game':
      return [
        category('beer', '🍺'),
        category('wine', '🍷'),
        category('spirits', '🥃'),
        category('cocktails', '🍹'),
        category('shots', '🍸'),
        category('fun-facts', '💡'),
      ];
    case 'stoned-yet':
      return [
        category('weed', '🌿'),
        category('culture', '🎨'),
        category('history', '📜'),
        category('science', '🔬'),
        category('fun-facts', '💡'),
      ];
    case 'rapid-fire':
      return [
        category('relationships', '💕'),
        category('pop-culture', '🎬'),
        category('embarrassing', '😳'),
        category('wild', '🔥'),
        category('funny', '😂'),
        category('quirky', '🤪'),
        category('sexy', '😏'),
        category('kinky', '😈'),
        category('all', '🎲'),
      ];
    case 'hot-takes':
      return [
        category('food', '🍕'),
        category('lifestyle', '🏠'),
        category('relationships', '💕'),
        category('culture', '🎭'),
        category('work', '💼'),
        category('social', '👥'),
        category('tech', '💻'),
        category('misc', '🎲'),
      ];
    case 'would-you-rather':
      return [
        category('food', '🍕'),
        category('lifestyle', '🏠'),
        category('relationships', '💕'),
        category('culture', '🎭'),
        category('work', '💼'),
        category('social', '👥'),
        category('tech', '💻'),
        category('embarrassing', '😳'),
        category('wild', '🔥'),
        category('misc', '🎲'),
      ];
    case 'what-could-it-be':
      return [
        CATEGORY_DEFINITIONS.nature,
        CATEGORY_DEFINITIONS.science,
        CATEGORY_DEFINITIONS.food,
        CATEGORY_DEFINITIONS.history,
        CATEGORY_DEFINITIONS.entertainment,
        CATEGORY_DEFINITIONS.geography,
        CATEGORY_DEFINITIONS.sports,
        CATEGORY_DEFINITIONS.music,
        CATEGORY_DEFINITIONS.technology,
        CATEGORY_DEFINITIONS.space,
        CATEGORY_DEFINITIONS.literature,
        CATEGORY_DEFINITIONS.movies,
        CATEGORY_DEFINITIONS.landmarks,
      ];
    case 'clue-in':
      return [
        CATEGORY_DEFINITIONS.scrum,
        CATEGORY_DEFINITIONS.kanban,
        CATEGORY_DEFINITIONS.agile,
        CATEGORY_DEFINITIONS.tech,
        CATEGORY_DEFINITIONS.business,
        CATEGORY_DEFINITIONS.culture,
        CATEGORY_DEFINITIONS.product,
        CATEGORY_DEFINITIONS.engineering,
      ];
    case 'what-am-i':
      return [
        CATEGORY_DEFINITIONS.nature,
        CATEGORY_DEFINITIONS.science,
        CATEGORY_DEFINITIONS.food,
        CATEGORY_DEFINITIONS.space,
        CATEGORY_DEFINITIONS.entertainment,
        CATEGORY_DEFINITIONS.geography,
        CATEGORY_DEFINITIONS.disney,
      ];
    case 'how-many':
      return [
        CATEGORY_DEFINITIONS.nature,
        CATEGORY_DEFINITIONS.science,
        CATEGORY_DEFINITIONS.food,
        CATEGORY_DEFINITIONS.space,
        CATEGORY_DEFINITIONS.geography,
        CATEGORY_DEFINITIONS.sports,
        CATEGORY_DEFINITIONS.disney,
        CATEGORY_DEFINITIONS.entertainment,
      ];
    case 'guess-what':
      return [
        category('beer', '🍺'),
        category('wine', '🍷'),
        category('cocktails', '🍹'),
        category('shots', '🍸'),
        category('fun-facts', '🎉'),
      ];
    case 'pair-matching':
    case 'team-pair-matching':
    case 'party-pair-matching':
    case 'family-pair-matching':
      return [
        CATEGORY_DEFINITIONS.nature,
        CATEGORY_DEFINITIONS.science,
        CATEGORY_DEFINITIONS.food,
        CATEGORY_DEFINITIONS.history,
        CATEGORY_DEFINITIONS.entertainment,
        CATEGORY_DEFINITIONS.geography,
        CATEGORY_DEFINITIONS.sports,
        CATEGORY_DEFINITIONS.cars,
        CATEGORY_DEFINITIONS.music,
        CATEGORY_DEFINITIONS.technology,
        CATEGORY_DEFINITIONS.space,
        CATEGORY_DEFINITIONS.literature,
        CATEGORY_DEFINITIONS.movies,
        CATEGORY_DEFINITIONS.art,
        CATEGORY_DEFINITIONS.mythology,
        CATEGORY_DEFINITIONS.inventions,
        CATEGORY_DEFINITIONS['world-records'],
        CATEGORY_DEFINITIONS.disney,
        CATEGORY_DEFINITIONS.landmarks,
      ];
    case 'agile-quiz':
      return [
        CATEGORY_DEFINITIONS.scrum,
        CATEGORY_DEFINITIONS.kanban,
        CATEGORY_DEFINITIONS.agile,
        CATEGORY_DEFINITIONS.devops,
        CATEGORY_DEFINITIONS.estimation,
        CATEGORY_DEFINITIONS.satire,
        CATEGORY_DEFINITIONS.tech,
        CATEGORY_DEFINITIONS.culture,
        CATEGORY_DEFINITIONS.metrics,
        CATEGORY_DEFINITIONS.engineering,
        CATEGORY_DEFINITIONS.product,
        CATEGORY_DEFINITIONS.security,
        CATEGORY_DEFINITIONS.architecture,
        CATEGORY_DEFINITIONS.design,
        CATEGORY_DEFINITIONS.testing,
        CATEGORY_DEFINITIONS.data,
        CATEGORY_DEFINITIONS.ai,
        CATEGORY_DEFINITIONS.hr,
        CATEGORY_DEFINITIONS.business,
        CATEGORY_DEFINITIONS.sales,
        CATEGORY_DEFINITIONS.marketing,
      ];
    case 'its-funny':
      return [
        CATEGORY_DEFINITIONS['office-humor'],
        CATEGORY_DEFINITIONS['remote-humor'],
        CATEGORY_DEFINITIONS['manager-humor'],
        CATEGORY_DEFINITIONS['dev-humor'],
        CATEGORY_DEFINITIONS['designer-humor'],
        CATEGORY_DEFINITIONS['executive-humor'],
        CATEGORY_DEFINITIONS['hr-humor'],
        CATEGORY_DEFINITIONS['recruiting-humor'],
        CATEGORY_DEFINITIONS['sales-humor'],
        CATEGORY_DEFINITIONS['startup-humor'],
        CATEGORY_DEFINITIONS['support-humor'],
      ];
    case 'odd-one-out':
      return [
        CATEGORY_DEFINITIONS.food,
        category('animals', '🐾'),
        CATEGORY_DEFINITIONS.nature,
        CATEGORY_DEFINITIONS.geography,
        CATEGORY_DEFINITIONS.science,
        CATEGORY_DEFINITIONS.space,
        CATEGORY_DEFINITIONS.technology,
        CATEGORY_DEFINITIONS.sports,
        CATEGORY_DEFINITIONS.music,
        CATEGORY_DEFINITIONS.movies,
        CATEGORY_DEFINITIONS.entertainment,
        CATEGORY_DEFINITIONS.history,
        category('words', '📝'),
        category('brands', '🏷️'),
        category('everyday', '🏠'),
        CATEGORY_DEFINITIONS.disney,
        CATEGORY_DEFINITIONS.art,
      ];
    case 'estimate-it':
      return [
        CATEGORY_DEFINITIONS.technology,
        CATEGORY_DEFINITIONS.business,
        CATEGORY_DEFINITIONS.history,
        CATEGORY_DEFINITIONS.agile,
        CATEGORY_DEFINITIONS.scrum,
        CATEGORY_DEFINITIONS.engineering,
        CATEGORY_DEFINITIONS.product,
        category('startups', '🚀'),
        category('workplace', '🏢'),
        category('internet', '🌐'),
        category('money', '💰'),
        CATEGORY_DEFINITIONS.science,
        category('tech', '💻'),
      ];
    case 'true-or-silly':
      return [
        category('animals', '🐾'),
        CATEGORY_DEFINITIONS.nature,
        CATEGORY_DEFINITIONS.science,
        CATEGORY_DEFINITIONS.space,
        CATEGORY_DEFINITIONS.food,
        CATEGORY_DEFINITIONS.geography,
        category('ocean', '🌊'),
        category('dinosaurs', '🦕'),
        category('human-body', '🫀'),
        CATEGORY_DEFINITIONS.inventions,
        CATEGORY_DEFINITIONS.disney,
        CATEGORY_DEFINITIONS.sports,
        category('words', '📝'),
      ];
    default:
      return [];
  }
};

// Check if a game type supports category voting
export const gameHasCategories = (gameType: string): boolean => {
  const categories = getCategoriesForGame(gameType);
  return categories.length > 0;
};
